// @ts-check

import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';
import { enumTypes, getEnumValue } from '../../../../../../../utils/enumHelper';


export default class CreateToolTypeDialogLogic {
  constructor() { }


  /**
   * Get initial values for create mode
   * @returns {import('./createToolTypeDialogTypes').CreateToolTypeInitialValues} - Initial form values
   */
  static getCreateModeInitialValues() {
    return {
      kitTypeConfiguration: getEnumValue(enumTypes.TOOL_CONFIGURATION_TYPE)('INTERCHANGEABLE'),
      maintenanceType: 'none'
    };
  }

  /**
   * Get initial values for edit mode
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited
   * @returns {import('./createToolTypeDialogTypes').CreateToolTypeInitialValues} - Initial form values populated with item data
   */
  static getEditModeInitialValues(itemToEdit) {


    // console.log('itemToEdit ===> ', itemToEdit);

    // Convert server maintenanceType format
    // const maintenanceType = this.convertServerMaintenanceType(itemToEdit.maintenanceType);
    /** @type {import('./createToolTypeDialogTypes').MaintenanceType} */
    let maintenanceType = itemToEdit.maintenanceType || 'none';

    // Temporary fix: If maintenanceType is TIME_BASED but interval is null, treat as 'none'
    // Note: interval of 0 is valid, so only check for null/undefined
    if (maintenanceType === 'TIME_BASED' && itemToEdit.maxMaintenanceIntervalTime == null) {
      maintenanceType = 'none';
    }

    const initialValues = {
      code: itemToEdit.businessId,
      name: { value: itemToEdit.id, label: itemToEdit.description || '', data: itemToEdit },
      kitTypeConfiguration: itemToEdit.configurationType,
      continuousProduction: itemToEdit.isContinuousProduction || false,
      maintenanceType: maintenanceType,
      // Maintenance scheduling fields
      operationName: itemToEdit.maintenanceOperation?.id || null,
      splitEnabled: itemToEdit.isMaintenanceSplittable || false,
      minHumanCapacity: itemToEdit.maintenanceMinHumanCapacity,
      maxHumanCapacity: itemToEdit.maintenanceMaxHumanCapacity,
      disallowedStations: itemToEdit.maintenanceDisallowedStations || []
    };

    // Add maintenance interval based on type
    this.addMaintenanceIntervalFields(initialValues, maintenanceType, itemToEdit);

    // Add scheduling duration if maintenance type is set
    this.addSchedulingDurationField(initialValues, maintenanceType, itemToEdit);

    // Add split-related fields if split is enabled
    this.addSplitFields(initialValues, itemToEdit);

    return initialValues;
  }

  /**
   * Add maintenance interval fields based on maintenance type
   * @param {import('./createToolTypeDialogTypes').CreateToolTypeInitialValues} initialValues - Initial values object to modify
   * @param {string} maintenanceType - The maintenance type
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited
   */
  static addMaintenanceIntervalFields(initialValues, maintenanceType, itemToEdit) {
    
    if (maintenanceType === 'none') {
      return;
    }

    
    if (maintenanceType === 'TIME_BASED') {

      if (!itemToEdit.maxMaintenanceIntervalTime) {
        return;
      }

      // maxMaintenanceIntervalTime is in minutes.
      const intervalObj = DateTimeHelper.ConvertMinutesToDaysHoursMinutesObject(itemToEdit.maxMaintenanceIntervalTime);

      // TimeField creates individual form fields with suffixes, not a single object
      // Note: Minutes are always set to 0 as UI only supports days:hours format
      initialValues.timeBasedInterval_days = intervalObj.days || 0;
      initialValues.timeBasedInterval_hours = intervalObj.hours || 0;
      initialValues.timeBasedInterval_minutes = 0;

      return;
    }

    
    if (maintenanceType === 'USAGE_BASED') {
      if (!itemToEdit.maxManufactureUsageTime) {
        return;
      }

      // maxManufactureUsageTime is in minutes.
      const intervalObj = DateTimeHelper.ConvertMinutesToDaysHoursMinutesObject(itemToEdit.maxManufactureUsageTime);

      // TimeField creates individual form fields with suffixes, not a single object
      // Note: Minutes are always set to 0 as UI only supports days:hours format
      initialValues.usageBasedInterval_days = intervalObj.days || 0;
      initialValues.usageBasedInterval_hours = intervalObj.hours || 0;
      initialValues.usageBasedInterval_minutes = 0;

      return;
    }

    
    if (maintenanceType === 'CYCLE_BASED') {
  
      if (!itemToEdit.maxNumOfCycles) {
        return;
      }
  
      // maxNumOfCycles is a simple number, not a time value
      initialValues.cycleBasedInterval = itemToEdit.maxNumOfCycles;
      
    }
  }

  
  /**
   * Add scheduling duration field if maintenance type is set
   * @param {import('./createToolTypeDialogTypes').CreateToolTypeInitialValues} initialValues - Initial values object to modify
   * @param {string} maintenanceType - The maintenance type
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited
   */
  static addSchedulingDurationField(initialValues, maintenanceType, itemToEdit) {
    if (maintenanceType === 'none' || !itemToEdit.maintenanceDuration) {
      return;
    }

    const schedulingDurationObj = DateTimeHelper.ConvertMinutesToHoursMinutesObject(itemToEdit.maintenanceDuration);
    // TimeField creates individual form fields with suffixes, not a single object
    initialValues.schedulingDuration_hours = schedulingDurationObj.hours || 0;
    initialValues.schedulingDuration_minutes = schedulingDurationObj.minutes || 0;
  }

  /**
   * Add split-related fields if split is enabled
   * @param {import('./createToolTypeDialogTypes').CreateToolTypeInitialValues} initialValues - Initial values object to modify
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited
   */
  static addSplitFields(initialValues, itemToEdit) {
    if (!itemToEdit.isMaintenanceSplittable) {
      return;
    }

    if (itemToEdit.maintenanceMinSplitBlock != null) {
      const minSplitObj = DateTimeHelper.ConvertMinutesToHoursMinutesObject(itemToEdit.maintenanceMinSplitBlock);
      // TimeField creates individual form fields with suffixes, not a single object
      initialValues.minimumSplitBlock_hours = minSplitObj.hours || 0;
      initialValues.minimumSplitBlock_minutes = minSplitObj.minutes || 0;
    }

    if (itemToEdit.maintenanceMaxTotalDuration != null) {
      const maxTotalObj = DateTimeHelper.ConvertMinutesToHoursMinutesObject(itemToEdit.maintenanceMaxTotalDuration);
      // TimeField creates individual form fields with suffixes, not a single object
      initialValues.maximumTotalDuration_hours = maxTotalObj.hours || 0;
      initialValues.maximumTotalDuration_minutes = maxTotalObj.minutes || 0;
    }
  }

  /**
   * Get initial state for edit mode
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited
   * @param {Object} currentState - Current component state
   * @returns {Object} - Updated state object
   */
  static getEditModeInitialState(itemToEdit, currentState) {
    // const maintenanceType = this.convertServerMaintenanceType(itemToEdit.maintenanceType);
    /** @type {import('./createToolTypeDialogTypes').MaintenanceType} */
    let maintenanceType = itemToEdit.maintenanceType || 'none';

    // Temporary fix: If maintenanceType is TIME_BASED but interval is null, treat as 'none'
    // Note: interval of 0 is valid, so only check for null/undefined
    if (maintenanceType === 'TIME_BASED' && itemToEdit.maxMaintenanceIntervalTime == null) {
      maintenanceType = 'none';
    }

    return {
      ...currentState,
      maintenanceType: maintenanceType,
      splitEnabled: itemToEdit.isMaintenanceSplittable || false,
      selectedOperation: itemToEdit.maintenanceOperation ? {
        value: itemToEdit.maintenanceOperation.id,
        label: itemToEdit.maintenanceOperation.displayName
      } : null
    };
  }

  /**
   * Prepare data for submission to server
   * @param {import('./createToolTypeDialogTypes').CreateToolTypeFormData} formData - Form data from Redux Form
   * @param {Function} createKitTypesData - Function to format kit types data
   * @param {boolean} isEditMode - Whether in edit mode
   * @param {import('mat/js/types/ServerTypes/Models/MasterData').ToolTypeResponseModel} itemToEdit - The item being edited (if edit mode)
   * @returns {Object} - Formatted data for server submission
   */
  static prepareSubmitData(formData, createKitTypesData, isEditMode, itemToEdit) {

    // console.log('formData ===> ', formData);

    const {
      name,
      toolCategory,
      code,
      kitTypeConfiguration,
      continuousProduction,
      maintenanceType,
      operationName,
      splitEnabled,
      // TimeField creates individual form fields with suffixes
      timeBasedInterval_days,
      timeBasedInterval_hours,
      timeBasedInterval_minutes,
      usageBasedInterval_days,
      usageBasedInterval_hours,
      usageBasedInterval_minutes,
      cycleBasedInterval,
      schedulingDuration_hours,
      schedulingDuration_minutes,
      minHumanCapacity,
      maxHumanCapacity,
      minimumSplitBlock_hours,
      minimumSplitBlock_minutes,
      maximumTotalDuration_hours,
      maximumTotalDuration_minutes,
      disallowedStations
    } = formData;

    const description = name ? name.label : '';

    // Calculate maintenance interval values based on maintenance type
    const maintenanceIntervalData = this.calculateMaintenanceIntervalData(
      maintenanceType,
      timeBasedInterval_days,
      timeBasedInterval_hours,
      timeBasedInterval_minutes,
      usageBasedInterval_days,
      usageBasedInterval_hours,
      usageBasedInterval_minutes,
      cycleBasedInterval
    );


    // Calculate maintenanceDuration
    const maintenanceDuration = this.calculateMaintenanceDuration(
      maintenanceType,
      schedulingDuration_hours,
      schedulingDuration_minutes
    );

    const newData = {
      id: isEditMode ? itemToEdit.id : undefined,
      businessId: code,
      toolCategoryId: toolCategory?.value || null,
      kitTypesWithCapacity: createKitTypesData(formData.kitTypes),
      description: description || null,
      maintenanceDuration,
      configurationType: kitTypeConfiguration,
      continuousProduction: continuousProduction,
      // Maintenance tab fields
      maintenanceType: maintenanceType !== 'none' ? maintenanceType : null,


      // Populate different fields based on maintenance type:
      // TIME_BASED
      maxMaintenanceIntervalTime: maintenanceIntervalData.maxMaintenanceIntervalTime,

      // USAGE_BASED
      maxManufactureUsageTime: maintenanceIntervalData.maxManufactureUsageTime,

      // CYCLE_BASED
      maxNumOfCycles: maintenanceIntervalData.maxNumOfCycles,


      // Scheduling fields
      maintenanceOperationId: operationName || null,
      maintenanceSplittable: splitEnabled || false,
      maintenanceMinSplitBlock: this.calculateSplitBlock(splitEnabled, minimumSplitBlock_hours, minimumSplitBlock_minutes),
      maintenanceMaxTotalDuration: this.calculateMaxTotalDuration(splitEnabled, maximumTotalDuration_hours, maximumTotalDuration_minutes),
      maintenanceMinHumanCapacity: minHumanCapacity || null,
      maintenanceMaxHumanCapacity: maxHumanCapacity || null,
      maintenanceDisallowedStations: disallowedStations || []

    };

    // // Add ID if in edit mode
    // if (isEditMode) {
    //   newData.id = itemToEdit.id;
    // }

    return newData;
  }

  /**
   * Calculate maintenance interval data based on type
   * Returns an object with the appropriate field populated based on maintenance type:
   * - TIME_BASED: { maxMaintenanceIntervalTime: <minutes> }
   * - USAGE_BASED: { maxManufactureUsageTime: <minutes> }
   * - CYCLE_BASED: { maxNumOfCycles: <cycles> }
   *
   * @param {string | undefined} maintenanceType - The maintenance type
   * @param {number | undefined} timeBasedInterval_days - Time-based interval days
   * @param {number | undefined} timeBasedInterval_hours - Time-based interval hours
   * @param {number | undefined} timeBasedInterval_minutes - Time-based interval minutes
   * @param {number | undefined} usageBasedInterval_days - Usage-based interval days
   * @param {number | undefined} usageBasedInterval_hours - Usage-based interval hours
   * @param {number | undefined} usageBasedInterval_minutes - Usage-based interval minutes
   * @param {number | undefined} cycleBasedInterval - Cycle-based interval value
   * 
   */
  static calculateMaintenanceIntervalData(
    maintenanceType,
    timeBasedInterval_days,
    timeBasedInterval_hours,
    timeBasedInterval_minutes,
    usageBasedInterval_days,
    usageBasedInterval_hours,
    usageBasedInterval_minutes,
    cycleBasedInterval
  ) {
    if (!maintenanceType || maintenanceType === 'none') {
      return {
        maxMaintenanceIntervalTime: null,
        maxManufactureUsageTime: null,
        maxNumOfCycles: null
      };
    }

    if (maintenanceType === 'TIME_BASED') {
      // TIME_BASED: populate maxMaintenanceIntervalTime
      const hasValue = timeBasedInterval_days || timeBasedInterval_hours || timeBasedInterval_minutes;
      const intervalMinutes = hasValue
        ? DateTimeHelper.ConvertDaysHoursMinutesToMinutes(
          timeBasedInterval_days || 0,
          timeBasedInterval_hours || 0,
          timeBasedInterval_minutes || 0
        )
        : null;

      return {
        maxMaintenanceIntervalTime: intervalMinutes,
        maxManufactureUsageTime: null,
        maxNumOfCycles: null
      };
    }

    if (maintenanceType === 'USAGE_BASED') {
      // USAGE_BASED: populate maxManufactureUsageTime
      const hasValue = usageBasedInterval_days || usageBasedInterval_hours || usageBasedInterval_minutes;
      const intervalMinutes = hasValue
        ? DateTimeHelper.ConvertDaysHoursMinutesToMinutes(
          usageBasedInterval_days || 0,
          usageBasedInterval_hours || 0,
          usageBasedInterval_minutes || 0
        )
        : null;

      return {
        maxMaintenanceIntervalTime: null,
        maxManufactureUsageTime: intervalMinutes,
        maxNumOfCycles: null
      };
    }

    if (maintenanceType === 'CYCLE_BASED') {
      // CYCLE_BASED: populate maxNumOfCycles
      return {
        maxMaintenanceIntervalTime: null,
        maxManufactureUsageTime: null,
        maxNumOfCycles: cycleBasedInterval || null
      };
    }

    return {
      maxMaintenanceIntervalTime: null,
      maxManufactureUsageTime: null,
      maxNumOfCycles: null
    };
  }

  /**
   * Calculate maintenance duration
   * @param {string | undefined} maintenanceType - The maintenance type
   * @param {number | undefined} schedulingDuration_hours - Scheduling duration hours
   * @param {number | undefined} schedulingDuration_minutes - Scheduling duration minutes
   * @returns {number | null} - Maintenance duration in minutes
   */
  static calculateMaintenanceDuration(maintenanceType, schedulingDuration_hours, schedulingDuration_minutes) {
    if (maintenanceType && maintenanceType === 'none') {
      return 0;
    }

    return DateTimeHelper.ConvertHoursMinutesToMinutes(
      schedulingDuration_hours,
      schedulingDuration_minutes
    );
  }

  /**
   * Calculate minimum split block time
   * @param {boolean | undefined} splitEnabled - Whether split is enabled
   * @param {number | undefined} minimumSplitBlock_hours - Minimum split block hours
   * @param {number | undefined} minimumSplitBlock_minutes - Minimum split block minutes
   * @returns {number|null} - Time in minutes or null
   */
  static calculateSplitBlock(splitEnabled, minimumSplitBlock_hours, minimumSplitBlock_minutes) {
    if (!splitEnabled) {
      return null;
    }

    if (minimumSplitBlock_hours == null && minimumSplitBlock_minutes == null) {
      return null;
    }

    return DateTimeHelper.ConvertHoursMinutesToMinutes(
      minimumSplitBlock_hours || 0,
      minimumSplitBlock_minutes || 0
    );
  }

  /**
   * Calculate maximum total duration
   * @param {boolean | undefined} splitEnabled - Whether split is enabled
   * @param {number | undefined} maximumTotalDuration_hours - Maximum total duration hours
   * @param {number | undefined} maximumTotalDuration_minutes - Maximum total duration minutes
   * @returns {number|null} - Time in minutes or null
   */
  static calculateMaxTotalDuration(splitEnabled, maximumTotalDuration_hours, maximumTotalDuration_minutes) {
    if (!splitEnabled) {
      return null;
    }

    if (maximumTotalDuration_hours == null && maximumTotalDuration_minutes == null) {
      return null;
    }

    return DateTimeHelper.ConvertHoursMinutesToMinutes(
      maximumTotalDuration_hours || 0,
      maximumTotalDuration_minutes || 0
    );
  }
}


